import React, { FC, useEffect, useRef, useState, KeyboardEvent } from 'react'
import clsx from 'clsx'
import { marked } from 'marked'
import katex from 'katex'
import 'katex/dist/katex.min.css'
import BeatLoaderImport from 'react-spinners/BeatLoader'

// react-spinners has no "exports" map in its package.json, so `react-spinners/BeatLoader`
// resolves as a plain CJS deep import. Depending on how a given bundle/chunk interops
// that CJS module for a default import, `BeatLoaderImport` can come through already
// unwrapped (a function) or still wrapped one level deeper (only `.default` is the
// function) - same class of bug as react-ga4/lottie-react. Resolve defensively so both
// shapes work.
const BeatLoader: typeof BeatLoaderImport =
  typeof BeatLoaderImport === 'function'
    ? BeatLoaderImport
    : (BeatLoaderImport as unknown as { default: typeof BeatLoaderImport }).default

import {
  toAbsoluteUrl,
  defaultMessages,
  defaultUserInfos,
  MessageModel,
  UserInfoModel,
} from '../../helpers'
import { createConversation, sendChatMessage, getConversationMessages } from '../../../app/api'
import { globalValue, useAppContext } from '../../../app/modules/apps/AppContext'
import { getEffectiveEdition, canUseModule } from '../../../app/modules/subscription/subscriptionAccess'
import { brandSettings } from '../../../app/modules/settings/BrandSettings'
import { useNavigate } from 'react-router-dom'
import { KTIcon } from '../../helpers'

type Props = {
  isDrawer?: boolean
}

function renderLaTeX(htmlContent: string): string {
  return htmlContent.replace(/\\\[(.*?)\\\]/g, (_, equation) =>
    katex.renderToString(equation, { throwOnError: false }),
  )
}

async function markdownToHtmlWithLaTeX(markdown: string): Promise<string> {
  const html = await marked.parse(markdown)
  return renderLaTeX(html)
}

function currentTime(): string {
  return new Date().toLocaleTimeString([], { hour: '2-digit', minute: '2-digit' })
}

async function convertStoredMessages(
  messages: { role: string; content: string; createdAt: string }[],
): Promise<MessageModel[]> {
  return Promise.all(
    messages.map(async (m) => {
      const isAssistant = m.role === 'assistant'
      const text = isAssistant ? await markdownToHtmlWithLaTeX(m.content) : m.content
      return {
        user: isAssistant ? 4 : 2,
        type: isAssistant ? ('in' as const) : ('out' as const),
        text,
        time: m.createdAt
          ? new Date(m.createdAt).toLocaleTimeString([], { hour: '2-digit', minute: '2-digit' })
          : currentTime(),
      }
    }),
  )
}

const ChatInner: FC<Props> = ({ isDrawer = false }) => {
  const [isThinking, setIsThinking] = useState(false)
  const [message, setMessage] = useState('')
  const [messages, setMessages] = useState<MessageModel[]>(defaultMessages)
  const [userInfos] = useState<UserInfoModel[]>(defaultUserInfos)
  const [conversationId, setConversationId] = useState<string>('')
  // Start locked for brands with subscription gating; unlock only once Premium is confirmed.
  const [isLocked, setIsLocked] = useState(brandSettings.showSubscription)

  const containerRef = useRef<HTMLDivElement>(null)
  const { subscriptionData } = useAppContext()
  const navigate = useNavigate()

  // Create or retrieve conversation once subscription is known and AI is available
  useEffect(() => {
    if (!brandSettings.showSubscription) {
      setIsLocked(false)
      initConversation()
      return
    }

    if (!subscriptionData) return

    const effectiveEdition = getEffectiveEdition(
      subscriptionData.currentPlan.edition,
      subscriptionData.currentPlan.status,
      subscriptionData.currentPlan.isTrial,
    )

    if (!canUseModule('AIAssistant', effectiveEdition)) {
      setIsLocked(true)
      return
    }

    setIsLocked(false)
    initConversation()
  // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [subscriptionData])

  // Scroll to bottom whenever messages change
  useEffect(() => {
    if (containerRef.current) {
      containerRef.current.scrollTop = containerRef.current.scrollHeight
    }
  }, [messages])

  async function initConversation() {
    if (globalValue.aiConversation) {
      setConversationId(globalValue.aiConversation)
      loadHistory(globalValue.aiConversation)
    } else {
      try {
        const result = await createConversation()
        if (result?.id) {
          globalValue.aiConversation = result.id
          setConversationId(result.id)
        }
      } catch (err) {
        console.error('Error creating conversation:', err)
      }
    }
  }

  async function loadHistory(id: string) {
    try {
      const stored = await getConversationMessages(id)
      if (Array.isArray(stored) && stored.length > 0) {
        const converted = await convertStoredMessages(stored)
        setMessages(converted)
      }
    } catch (err) {
      console.error('Error loading conversation history:', err)
    }
  }

  const sendMessage = async () => {
    if (!conversationId || !message.trim()) return

    const userText = message
    setMessage('')
    setIsThinking(true)

    setMessages((prev) => [
      ...prev,
      { user: 2, type: 'out', text: userText, time: currentTime() },
    ])

    try {
      const response = await sendChatMessage(conversationId, userText)
      const htmlText = await markdownToHtmlWithLaTeX(response)
      setMessages((prev) => [
        ...prev,
        { user: 4, type: 'in', text: htmlText, time: currentTime() },
      ])
    } catch {
      setMessages((prev) => [
        ...prev,
        { user: 4, type: 'in', text: 'Something went wrong!', time: currentTime() },
      ])
    } finally {
      setIsThinking(false)
    }
  }

  const onEnterPress = (e: KeyboardEvent<HTMLTextAreaElement>) => {
    if (e.key === 'Enter' && !e.shiftKey) {
      e.preventDefault()
      sendMessage()
    }
  }

  return (
    <div className={clsx('d-flex flex-column vh-100 overflow-hidden')} style={{ position: 'relative' }}>
      {isLocked && (
        <div
          style={{
            position: 'absolute',
            inset: 0,
            backdropFilter: 'blur(4px)',
            backgroundColor: 'rgba(255,255,255,0.6)',
            zIndex: 10,
            display: 'flex',
            flexDirection: 'column',
            alignItems: 'center',
            justifyContent: 'center',
            gap: '16px',
            padding: '32px',
            textAlign: 'center',
          }}
        >
          <div className='w-60px h-60px rounded-circle bg-light-warning d-flex align-items-center justify-content-center'>
            <KTIcon iconName='lock' className='text-warning fs-1' />
          </div>
          <div>
            <div className='fw-bold fs-5 text-gray-800 mb-2'>
              {'AI Assistant is a Premium feature'.toTranslatedDisplay()}
            </div>
            <div className='text-muted fs-7'>
              {'Upgrade your plan to chat with the AI accounting assistant.'.toTranslatedDisplay()}
            </div>
          </div>
          <button
            className='btn btn-primary btn-sm'
            onClick={() => navigate('/settings/subscription')}
          >
            {'View Plans'.toTranslatedDisplay()}
          </button>
        </div>
      )}

      <div
        className='card-body flex-grow-1 overflow-auto'
        id={isDrawer ? 'kt_drawer_chat_messenger_body' : 'kt_chat_messenger_body'}
        ref={containerRef}
        data-kt-element='messages'
      >
        {messages.map((msg, index) => {
          const userInfo = msg.type === 'in' ? userInfos[4] : userInfos[2]
          const state = msg.type === 'in' ? 'info' : 'primary'
          const templateAttr: Record<string, string> = {}

          if (msg.template) {
            Object.defineProperty(templateAttr, 'data-kt-element', {
              value: `template-${msg.type}`,
              enumerable: true,
            })
          }

          const contentClass = clsx(
            isDrawer ? '' : 'd-flex',
            `justify-content-${msg.type === 'in' ? 'start' : 'end'} mb-10`,
            { 'd-none': msg.template },
          )

          return (
            <div
              key={`message${index}`}
              className={clsx('d-flex', contentClass, 'mb-10')}
              {...templateAttr}
            >
              <div
                className={clsx(
                  'd-flex flex-column align-items',
                  `align-items-${msg.type === 'in' ? 'start' : 'end'}`,
                )}
              >
                <div className='d-flex align-items-center mb-2'>
                  {msg.type === 'in' ? (
                    <>
                      <div className='symbol p-3 symbol-35px symbol-circle'>
                        <img
                          alt='Pic'
                          className='img-thumbnail'
                          src={toAbsoluteUrl(`media/${userInfo.avatar}`)}
                        />
                      </div>
                      <div className='ms-3'>
                        <a
                          href='#'
                          className='fs-5 fw-bolder text-gray-900 text-hover-primary me-1'
                        >
                          {userInfo.name}
                        </a>
                        <span className='text-muted fs-7 mb-1'>{msg.time}</span>
                      </div>
                    </>
                  ) : (
                    <>
                      <div className='me-3'>
                        <span className='text-muted fs-7 mb-1'>{msg.time}</span>
                        <a
                          href='#'
                          className='fs-5 fw-bolder text-gray-900 text-hover-primary ms-1'
                        >
                          {'You'.toTranslatedDisplay()}
                        </a>
                      </div>
                      <div className='symbol symbol-35px symbol-circle'>
                        <img alt='Pic' src={toAbsoluteUrl(`media/${userInfo.avatar}`)} />
                      </div>
                    </>
                  )}
                </div>

                <div
                  className={clsx(
                    'p-5 rounded',
                    `bg-light-${state}`,
                    'text-gray-900 fw-bold mw-lg-400px',
                    `text-${msg.type === 'in' ? 'start' : 'end'}`,
                  )}
                  data-kt-element='message-text'
                  dangerouslySetInnerHTML={{ __html: msg.text }}
                />
              </div>
            </div>
          )
        })}

        {isThinking && (
          <div className='d-flex'>
            <BeatLoader size={8} />
            <p className='ms-2 text-muted'>Thinking</p>
          </div>
        )}
      </div>

      <div
        className='p-1 mt-3'
        id={isDrawer ? 'kt_drawer_chat_messenger_footer' : 'kt_chat_messenger_footer'}
      >
        <div className='d-flex flex-stack justify-items-center m-5'>
          <textarea
            className='form-control form-control-flush border'
            rows={1}
            data-kt-element='input'
            placeholder={'Type a message'.toTranslatedDisplay()}
            value={message}
            onChange={(e) => setMessage(e.target.value)}
            onKeyDown={onEnterPress}
            disabled={isThinking}
          />
          <button
            className='ms-3 btn btn-primary'
            type='button'
            data-kt-element='send'
            onClick={sendMessage}
            disabled={isThinking}
          >
            {'Send'.toTranslatedDisplay()}
          </button>
        </div>
        <div className='w-100 text-center'>
          <p className='text-muted text-center'>
            {'Ai Agent can make mistakes!'.toTranslatedDisplay()}
            {' '}
            <a
              href='https://mybooksai.app/help/ai-agent/ai-agent-info/'
              target='_blank'
              rel='noopener noreferrer'
              className='ms-2'
            >
              {'Learn more'.toTranslatedDisplay()}
            </a>
          </p>
        </div>
      </div>
    </div>
  )
}

export { ChatInner }
